import { useMemo, useState } from 'react'
import { postDataAPI } from '../../../lib/api'
import { discoverAndBulkImport } from '../../../lib/catalogBulkImport'
import { importBandsintownViaAI } from '../../../lib/bandsintownAgent'
import { uploadImage } from '../../../lib/uploadImage'
import { trackSignup } from '../../../lib/analytics'
import type { SignupUser } from '../SignupPage'
import OnboardingShell from './OnboardingShell'
import { useNavigate } from 'react-router-dom'
import { OnboardingStep } from './steps'

export default function Step06Avatar({
  setStep,
  user,
  setUser,
  from,
}: {
  setStep: (n: number) => void
  user: SignupUser
  setUser: React.Dispatch<React.SetStateAction<SignupUser>>
  from: string
}) {
  const navigate = useNavigate()
  const [file, setFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const preview = useMemo(() => {
    if (file) return URL.createObjectURL(file)
    if (typeof user.avatar === 'string') return user.avatar
    return null
  }, [file, user.avatar])

  const submit = async () => {
    setError(null)
    if (!user.email) return setError('Email is required')
    if (!user.professionRole) return setError('Role is required')
    if (!user.username) return setError('Username is required')

    setIsSubmitting(true)
    try {
      let avatarUrl: string | null = typeof user.avatar === 'string' ? user.avatar : null
      if (file) {
        avatarUrl = await uploadImage(file)
        if (!avatarUrl) throw new Error('Image upload failed')
      }

      const userToSubmit: any = { ...user, avatar: avatarUrl }
      const res = await postDataAPI('auth/add_user_info_personal', userToSubmit)
      if (res?.status !== 200) throw new Error(res?.message || 'Failed to create account')

      const createdUser = res?.data?.user
      const token = res?.data?.token
      if (token) localStorage.setItem('dizeeToken', JSON.stringify(token))
      if (createdUser) localStorage.setItem('dizeeUser', JSON.stringify(createdUser))
      trackSignup('email')
      localStorage.setItem('isNewSignup', 'true')

      // Claim artist-invite if user signed up via invite link
      const inviteToken = user?.artistInviteToken
      if (inviteToken && token) {
        try {
          await postDataAPI('team/claim-artist-invite', { token: inviteToken }, token)
        } catch (err) {
          console.warn('Claim artist invite failed:', err)
        }
      }

      const spotifyUrl = user?.social?.sf
      if (spotifyUrl) {
        localStorage.setItem('catalogImportInProgress', 'true')
        discoverAndBulkImport(spotifyUrl, user?.name || user?.username || '', {
          batchSize: 10,
          batchDelay: 300,
          timeout: 300000,
        })
          .then(() => {
            localStorage.removeItem('catalogImportInProgress')
          })
          .catch((err) => {
            console.warn('Onboarding catalog discovery failed:', err?.message || err)
            localStorage.removeItem('catalogImportInProgress')
          })
      }

      const pendingArtistName = localStorage.getItem('pendingBandsintownImport')
      if (pendingArtistName) {
        localStorage.setItem('bandsintownImportInProgress', 'true')
        localStorage.setItem('bandsintownImportStartedAt', String(Date.now()))
        importBandsintownViaAI(pendingArtistName)
          .then((r) => {
            if (r?.success && (r as any)?.savedCount > 0) {
              localStorage.setItem('bandsintownImportSuccess', String((r as any).savedCount))
            }
          })
          .finally(() => {
            localStorage.removeItem('pendingBandsintownImport')
            localStorage.removeItem('bandsintownImportInProgress')
            localStorage.removeItem('bandsintownImportStartedAt')
          })
      }

      navigate(user?.artistInviteToken ? '/shows' : (from || '/'), { replace: true })
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to create account')
    } finally {
      setIsSubmitting(false)
    }
  }

  const initials = (user?.name || user?.username || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('')

  return (
    <OnboardingShell
      title={preview ? 'Change profile picture' : 'Upload a profile picture'}
      footer="This will be displayed on your profile and in the sidebar."
    >
      <div className="space-y-5">
        <div className="flex justify-center">
          {preview ? (
            <img
              src={preview}
              alt="avatar preview"
              className="h-20 w-20 rounded-full object-cover border border-gray-200"
            />
          ) : (
            <div className="h-20 w-20 rounded-full bg-gray-100 text-gray-600 text-lg font-semibold flex items-center justify-center border border-gray-200">
              {initials || '?'}
            </div>
          )}
        </div>

        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const f = e.target.files?.[0] || null
            setFile(f)
            if (f) setUser((prev) => ({ ...prev, avatar: f }))
          }}
          className="block w-full text-sm text-gray-500 outline-none file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-solid file:border-gray-300 file:bg-white file:text-gray-900 file:outline-none file:ring-0 file:ring-offset-0 dark:file:border-gray-600 dark:file:bg-gray-800 dark:file:text-gray-100"
        />

        {error ? <div className="text-red-600 text-sm">{error}</div> : null}

        <div className="flex gap-4">
          <button type="button" onClick={() => setStep(OnboardingStep.Username)} className="ds-btn ds-btn-secondary flex-1">
            Go back
          </button>
          <button type="button" disabled={isSubmitting} onClick={submit} className="ds-btn ds-btn-primary flex-1">
            {isSubmitting ? 'Creating…' : 'Complete'}
          </button>
        </div>
      </div>
    </OnboardingShell>
  )
}

